import React, { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { Plus, BookOpen, Pencil, Trash2, Layers, Eye, X, Check, EyeOff } from 'lucide-react'
import ApplicationLayout from '../leevo_ui/ApplicationLayout/ApplicationLayout'
import LoadingSpinner from '../leevo_ui/LoadingSpinner/LoadingSpinner'
import EmptyList from '../leevo_ui/EmptyList/EmptyList'
import ConfirmationDialog from '../leevo_ui/ConfirmationDialog/ConfirmationDialog'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { Checkbox } from '../ui/checkbox'
import { requestCreator } from '../../utilities/requests'
import { SPORT_OPTIONS, getSportLabel } from '../../utilities/sportOptions'
import toast from '../../utilities/toast'
import useFacility from '../../utilities/hooks/useFacility'
import emptyCalendar from '../../images/empty-calendar.svg'
import SkillLevelDrawer from './SkillLevelDrawer/SkillLevelDrawer'

const {
  get: getCurriculums,
  post: createCurriculum,
  put: updateCurriculum,
  delete: deleteCurriculum,
  cancel: cancelRequests
} = requestCreator()

function FacilitiesCurriculumsIndex() {
  const { facility_id: facilityId } = useParams()
  const { isLoading: isFacilityLoading } = useFacility()
  const [curriculums, setCurriculums] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [expandedId, setExpandedId] = useState(null)
  const [expandedMode, setExpandedMode] = useState(null)
  const [isSaving, setIsSaving] = useState(false)
  const [selectedCurriculum, setSelectedCurriculum] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [curriculumToDelete, setCurriculumToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [hideShared, setHideShared] = useState(false)

  const [formData, setFormData] = useState({
    name: '',
    sport: '',
    requiresLtsNumber: false
  })

  const loadCurriculums = useCallback(async () => {
    setIsLoading(true)
    try {
      const data = await getCurriculums(`/api/facilities/${facilityId}/curriculums`, {
        params: { include: 'skill_levels' }
      })
      setCurriculums(data)
    } catch (error) {
      if (!axios.isCancel(error)) {
        toast.error('Failed to load curriculums')
      }
    } finally {
      setIsLoading(false)
    }
  }, [facilityId])

  useEffect(() => {
    if (!isFacilityLoading && facilityId) {
      loadCurriculums()
    }
    return cancelRequests
  }, [isFacilityLoading, facilityId, loadCurriculums])

  function handleAddNew() {
    setExpandedId('new')
    setExpandedMode('new')
    setFormData({
      name: '',
      sport: '',
      requiresLtsNumber: false
    })
  }

  function handleEdit(curriculum) {
    setExpandedId(curriculum.id)
    setExpandedMode('edit')
    setFormData({
      name: curriculum.name,
      sport: curriculum.sport,
      requiresLtsNumber: curriculum.requiresLtsNumber || false
    })
  }

  function handleDeleteClick(curriculum) {
    setCurriculumToDelete(curriculum)
  }

  function handleCancel() {
    setExpandedId(null)
    setExpandedMode(null)
    setFormData({
      name: '',
      sport: '',
      requiresLtsNumber: false
    })
  }

  async function handleSave() {
    if (!formData.name || !formData.sport) {
      toast.error('Please fill in all required fields')
      return
    }

    setIsSaving(true)
    try {
      const payload = {
        curriculum: {
          name: formData.name,
          sport: formData.sport,
          requires_lts_number: formData.requiresLtsNumber
        }
      }

      if (expandedMode === 'new') {
        await createCurriculum(`/api/facilities/${facilityId}/curriculums`, payload)
        toast.success('Curriculum created successfully')
      } else {
        await updateCurriculum(`/api/facilities/${facilityId}/curriculums/${expandedId}`, payload)
        toast.success('Curriculum updated successfully')
      }

      handleCancel()
      loadCurriculums()
    } catch (error) {
      toast.error('Failed to save curriculum')
    } finally {
      setIsSaving(false)
    }
  }

  async function handleDeleteConfirm() {
    setIsDeleting(true)
    try {
      await deleteCurriculum(`/api/facilities/${facilityId}/curriculums/${curriculumToDelete.id}`)
      toast.success('Curriculum deleted successfully')
      setCurriculumToDelete(null)
      loadCurriculums()
    } catch (error) {
      toast.error('Failed to delete curriculum. It may be in use by classes.')
    } finally {
      setIsDeleting(false)
    }
  }

  function handleManageLevels(curriculum) {
    setSelectedCurriculum(curriculum)
    setDrawerOpen(true)
  }

  function handleDrawerClose() {
    setDrawerOpen(false)
    setSelectedCurriculum(null)
  }

  function getSkillLevelCount(curriculum) {
    return curriculum.skillLevels?.length || 0
  }

  function getClassesCount(curriculum) {
    return curriculum.classesCount || 0
  }

  const sharedCurriculums = curriculums.filter((c) => !c.isFacilityOwned)
  const ownedCurriculums = curriculums.filter((c) => c.isFacilityOwned)
  const displayedCurriculums = hideShared ? ownedCurriculums : curriculums

  function renderCurriculumCard(curriculum) {
    const isExpanded = expandedId === curriculum.id
    const isEditing = isExpanded && expandedMode === 'edit'
    const isOwned = curriculum.isFacilityOwned

    return (
      <div key={curriculum.id} className="bg-white rounded-lg shadow border border-gray-200 overflow-hidden">
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
              <BookOpen className="h-5 w-5 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-base font-semibold text-gray-900">{curriculum.name}</h3>
                {!isOwned && (
                  <span className="px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded-full flex-shrink-0">
                    Shared
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500">
                {getSportLabel(curriculum.sport)} · {getSkillLevelCount(curriculum)} levels ·{' '}
                {getClassesCount(curriculum)} classes
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => handleManageLevels(curriculum)}
              className="text-purple-600 border-purple-200 hover:bg-purple-50"
            >
              {isOwned ? <Layers className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
              {isOwned ? 'Manage Levels' : 'View Levels'}
            </Button>
            {isOwned && (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleEdit(curriculum)}
                  disabled={isExpanded}
                  className="text-gray-600 hover:text-purple-600 hover:bg-purple-50"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteClick(curriculum)}
                  disabled={isExpanded}
                  className="text-gray-600 hover:text-red-600 hover:bg-red-50"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>

        {isEditing && (
          <div className="px-4 py-4 bg-purple-50 border-t border-purple-100">
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor={`edit-name-${curriculum.id}`} className="block mb-1 text-sm font-medium">
                    Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id={`edit-name-${curriculum.id}`}
                    type="text"
                    placeholder="e.g., Learn to Skate USA"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full bg-white"
                  />
                </div>
                <div>
                  <Label htmlFor={`edit-sport-${curriculum.id}`} className="block mb-1 text-sm font-medium">
                    Sport <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.sport}
                    onValueChange={(value) =>
                      setFormData({
                        ...formData,
                        sport: value,
                        requiresLtsNumber: value !== 'skating' ? false : formData.requiresLtsNumber
                      })
                    }
                  >
                    <SelectTrigger id={`edit-sport-${curriculum.id}`} className="bg-white">
                      <SelectValue placeholder="Select a sport" />
                    </SelectTrigger>
                    <SelectContent>
                      {SPORT_OPTIONS.map((sport) => (
                        <SelectItem key={sport.value} value={sport.value}>
                          {sport.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {formData.sport === 'skating' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`edit-lts-${curriculum.id}`}
                    checked={formData.requiresLtsNumber}
                    onCheckedChange={(checked) => setFormData({ ...formData, requiresLtsNumber: checked })}
                  />
                  <Label htmlFor={`edit-lts-${curriculum.id}`} className="font-normal cursor-pointer text-sm">
                    Requires LTS/USFSA Member Number
                  </Label>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </Button>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaving}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  <Check className="h-4 w-4 mr-1" />
                  {isSaving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  function renderNewCurriculumForm() {
    if (expandedId !== 'new') return null

    return (
      <div className="bg-white rounded-lg shadow border border-purple-200 overflow-hidden mb-4">
        <div className="px-4 py-3 border-b border-purple-200 bg-purple-100">
          <h3 className="text-sm font-semibold text-purple-900">New Curriculum</h3>
        </div>
        <div className="px-4 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="new-name" className="block mb-1 text-sm font-medium">
                Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="new-name"
                type="text"
                placeholder="e.g., Learn to Skate USA"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full"
              />
            </div>
            <div>
              <Label htmlFor="new-sport" className="block mb-1 text-sm font-medium">
                Sport <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.sport}
                onValueChange={(value) =>
                  setFormData({
                    ...formData,
                    sport: value,
                    requiresLtsNumber: value !== 'skating' ? false : formData.requiresLtsNumber
                  })
                }
              >
                <SelectTrigger id="new-sport">
                  <SelectValue placeholder="Select a sport" />
                </SelectTrigger>
                <SelectContent>
                  {SPORT_OPTIONS.map((sport) => (
                    <SelectItem key={sport.value} value={sport.value}>
                      {sport.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {formData.sport === 'skating' && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="new-lts"
                checked={formData.requiresLtsNumber}
                onCheckedChange={(checked) => setFormData({ ...formData, requiresLtsNumber: checked })}
              />
              <Label htmlFor="new-lts" className="font-normal cursor-pointer text-sm">
                Requires LTS/USFSA Member Number
              </Label>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel} disabled={isSaving}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleSave}
              disabled={isSaving}
              className="bg-purple-600 hover:bg-purple-700"
            >
              <Check className="h-4 w-4 mr-1" />
              {isSaving ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <ApplicationLayout>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-6 pt-6">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Curriculums</h1>
            {sharedCurriculums.length > 0 && (
              <button
                type="button"
                onClick={() => setHideShared(!hideShared)}
                className="text-sm border border-gray-200 rounded-md px-2 py-1 text-gray-700 bg-white hover:text-purple-600 flex items-center gap-1 mt-1"
              >
                {hideShared ? (
                  <>
                    <Eye className="h-3.5 w-3.5" />
                    Show {sharedCurriculums.length} shared curriculum{sharedCurriculums.length !== 1 ? 's' : ''}
                  </>
                ) : (
                  <>
                    <EyeOff className="h-3.5 w-3.5" />
                    Hide shared curriculums
                  </>
                )}
              </button>
            )}
          </div>
          <Button onClick={handleAddNew} disabled={expandedId === 'new'} className="bg-purple-600 hover:bg-purple-700">
            <Plus className="h-4 w-4 mr-1" />
            Add Curriculum
          </Button>
        </div>
      </div>

      {isLoading ? (
        <LoadingSpinner size="1/5" className="flex items-center justify-center h-full" />
      ) : (
        <div className="flex flex-col flex-1 bg-gray-100">
          <div className="w-full max-w-7xl mx-auto px-4 py-4">
            {renderNewCurriculumForm()}

            {displayedCurriculums.length === 0 && expandedId !== 'new' ? (
              <div className="bg-white rounded-lg shadow">
                <div className="flex items-center justify-center py-12">
                  <EmptyList
                    message={
                      hideShared
                        ? 'No custom curriculums yet. Create one or show shared curriculums.'
                        : "You don't have any curriculums yet. Create a curriculum to define skill levels and skills for your programs."
                    }
                    svgSrc={emptyCalendar}
                    buttonText="Create Curriculum"
                    onButtonClick={handleAddNew}
                  />
                </div>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {displayedCurriculums.map(renderCurriculumCard)}
              </div>
            )}
          </div>
        </div>
      )}

      <SkillLevelDrawer
        open={drawerOpen}
        onOpenChange={handleDrawerClose}
        curriculum={selectedCurriculum}
        facilityId={facilityId}
        readOnly={selectedCurriculum && !selectedCurriculum.isFacilityOwned}
        onSkillLevelsUpdated={loadCurriculums}
      />

      <ConfirmationDialog
        isOpen={!!curriculumToDelete}
        onClose={() => setCurriculumToDelete(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete Curriculum?"
        message={
          curriculumToDelete
            ? `Deleting "${curriculumToDelete.name}" will permanently remove it and all its skill levels. Classes using this curriculum may be affected. This action cannot be undone.`
            : ''
        }
        confirmText="Delete Curriculum"
        cancelText="Cancel"
        confirmColor="red"
        isLoading={isDeleting}
      />
    </ApplicationLayout>
  )
}

export default FacilitiesCurriculumsIndex
